/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { Component, Type } from '@angular/core';

import {
    AviPermissionResource,
    IArchivePolicy,
    ITechSupportProfile,
} from 'generated-types';

import { TechSupportProfile } from 'object-types';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { MessageItem, ObjectTypeItem } from 'ajs/modules/data-model/factories';
import { withFullModalMixin } from 'ajs/js/utilities/mixins';

interface ITechSupportProfileData {
    config: ITechSupportProfile;
}

export type TTechSupportProfileItem = typeof TechSupportProfileItem;

/**
 * @description Tech-Support-Profile Item
 * @author Dnyaneshwar Jadhav
 */
export class TechSupportProfileItem extends withFullModalMixin(ObjectTypeItem) {
    public getConfig: () => ITechSupportProfile;
    public data: ITechSupportProfileData;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'techsupportprofile',
            objectType: TechSupportProfile,
            windowElement: 'lazy-load',
            permissionName: AviPermissionResource.PERMISSION_TECHSUPPORTPROFILE,
            id: 'default',
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Import lazy-loaded modal component.
     */
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            TechSupportProfileModalComponent,
        } = await import(
            /* webpackChunkName: "update-profile-modal" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/tech-support-profile-modal/tech-support-profile-modal.component'
        );

        return TechSupportProfileModalComponent as Type<Component>;
    }

    /** @override */
    public urlToSave(): string {
        return `/api/${this.objectName}`;
    }

    /** @override */
    public async loadConfig(): Promise<ng.IHttpResponse<ITechSupportProfile>> {
        const url = `/api/${this.objectName}`;

        this.cancelRequests('config');

        const response: ng.IHttpResponse<ITechSupportProfile> =
            await this.request('get', url, undefined, null, 'config');

        this.onConfigLoad_(response);

        return response;
    }

    /**
     * Remove Archive Rule config item.
     */
    public addArchiveRule(): void {
        this.config.archive_rules.config.rules.add();
    }

    /**
     * Remove Archive Rule config item.
     */
    public removeArchiveRule(rule: MessageItem<IArchivePolicy>): void {
        this.config.archive_rules.config.rules.removeByMessageItem(rule);
    }

    /**
     * Method will load required Field on add or edit action.
     * @override
     */
    protected requiredFields(): string[] {
        return [
            'event_params',
        ];
    }
}
